import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { catchError, map, Observable, of, Subject, takeUntil } from 'rxjs';
import { AccountService } from 'src/app/services/account.service';
import { MapService } from 'src/app/services/map.service';
import { OtherService } from 'src/app/services/other.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-specialist-registration',
  templateUrl: './specialist-registration.component.html',
  styleUrls: ['./specialist-registration.component.css']
})

export class SpecialistRegistrationComponent implements OnInit {

  private persianRegex = /^[\u0600-\u06FF\s]$/;

  showMap: boolean = false;

  isSubmitDisabled = false;
  @Input() editId: number | null = null;
  @Input() embedded = false;
  @Output() closeEdit = new EventEmitter<void>();
  @Output() savedEdit = new EventEmitter<any>();
  selectedState = '';
  isLoadingEdit = false;
  canEdit = false;
  installationStatus = '';

  formGroup = new FormGroup({
    strName: new FormControl('', [Validators.required]),
    strFamily: new FormControl('', [Validators.required]),
    strID: new FormControl('', [Validators.required, this.validateStrID()]),
    strMobile: new FormControl('', [Validators.required, this.validateStrMobile()]),
    strTel: new FormControl(''),
    iYearConstruction: new FormControl('', Validators.required),
    strVIN: new FormControl('', [Validators.required, Validators.minLength(17), Validators.maxLength(17)]),
    strLicenseNo: new FormControl(''),
    tiMobileGroup: new FormControl(null, [Validators.required]),
    strCity: new FormControl(null, [Validators.required]),
    strLicenseImage: new FormControl(''),
    strAddress: new FormControl(''),
    strComment: new FormControl(''),
    strColor: new FormControl(''),
    iCost: new FormControl('', [(control: AbstractControl) => !control.value || (/^\d{1,3}(,\d{3})*$/.test(control.value) && Number.isSafeInteger(Number(control.value.replace(/,/g, '')))) ? null : { amount: true }]),
    plak4: new FormControl('', [Validators.required, Validators.maxLength(2), Validators.pattern(/^[1-9][0-9]$/)]),
    plak3: new FormControl('', [Validators.required, Validators.maxLength(3), Validators.pattern(/^[1-9][0-9][0-9]$/)]),
    plak2: new FormControl('ع', [Validators.required]),
    plak1: new FormControl('', [Validators.required, Validators.maxLength(2), Validators.pattern(/^[1-9][0-9]$/)]),
  });

  coordinates = {
    fLat: 0,
    fLon: 0
  };

  showMapDialog = false;

  carTypes$ = this.accountService.tankerGPSType().pipe(
    map((response: string) => JSON.parse(response)),
    catchError(() => of(false)),
  );

  states$ = this.accountService.GetState().pipe(
    map((response: string) => JSON.parse(response)),
    catchError(() => of(false)),
  );

  cities$: Observable<any>;

  unSubscribeAll$ = new Subject<void>();

  constructor(
    private accountService: AccountService,
    private otherService: OtherService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private mapService: MapService,
    private ngbModal: NgbModal,
  ) { }

  ngOnInit(): void {
    if (this.showMap) {
      this.formGroup.get('strAddress').addValidators(Validators.required);
    }

    const edit = this.editId ?? this.activatedRoute.snapshot.queryParamMap.get('edit');
    if (edit !== null) {
      const id = Number(edit);
      if (!Number.isSafeInteger(id) || id <= 0) {
        this.accountService.toastSwal('error', 'شماره پیش‌ثبت‌نام معتبر نیست');
        if (this.embedded) this.closeEdit.emit();
        else this.router.navigate(['/specialist-verification']);
        return;
      }
      this.editId = id;
      this.formGroup.get('strMobile').disable();
      this.formGroup.addControl('strIMEI', new FormControl(''));
      this.formGroup.addControl('strCellNo', new FormControl(''));
      this.formGroup.addControl('strPaymentComment', new FormControl(''));
      this.formGroup.addControl('strSignatureImage', new FormControl(''));
      this.loadEdit();
      return;
    }

    const strMobile = this.activatedRoute.snapshot.queryParamMap.get('mobile');
    const isValid = this.otherService.isValidMobilePhoneNumber(strMobile);

    if (isValid) {
      const strMobileFormControl = this.formGroup.get('strMobile');
      strMobileFormControl.setValue(strMobile);
      strMobileFormControl.disable();
    }
  }

  async submit() {

    if (this.isSubmitDisabled || (this.editId !== null && !this.canEdit)) return;
    if (this.formGroup.invalid) {
      if (this.editId !== null) this.formGroup.markAllAsTouched();
      return;
    }
    if (this.editId === null && this.formGroup.pristine) {
      return;
    }

    const confirmDialog = await Swal.fire({
      title: this.editId !== null ? 'آیا از ویرایش اطلاعات اطمینان دارید؟' : 'آیا از ثبت نام اطمینان دارید؟',
      icon: 'warning',
      showCancelButton: true,
      showConfirmButton: true,
      reverseButtons: true,
      customClass: { container: 'specialist-confirm-dialog' },
      confirmButtonColor: '#10b981',
      confirmButtonText: 'تایید',
      cancelButtonText: 'انصراف',
    });

    if (!confirmDialog.isConfirmed) {
      return;
    }

    this.isSubmitDisabled = true;

    const formValue = this.formGroup.getRawValue();

    const strVehicleNo = `${formValue.plak1} ${formValue.plak2} ${formValue.plak3} | ${formValue.plak4}`;
    delete formValue.plak1;
    delete formValue.plak2;
    delete formValue.plak3;
    delete formValue.plak4;
    const amount = Number(formValue.iCost.replace(/,/g, ''));
    delete formValue.iCost;

    const model = {
      ...this.coordinates,
      ...formValue,
      strVehicleNo,
      iSystemUser: this.otherService.specialistUser.iSystemUser,
      strSession: this.otherService.specialistUser.strSession
    };

    if (this.editId !== null && typeof model.strSignatureImage === 'string' && model.strSignatureImage.startsWith('data:')) {
      model.strSignatureImage = model.strSignatureImage.split(',')[1] || '';
    }

    const request = this.editId !== null
      ? this.accountService.preregistrationUpdateByAdmin({ ...model, iPreregistration: this.editId, iCost: amount })
      : this.accountService.specialistRegistration({ ...model, iPayment1: amount });
    request.pipe(
      catchError(() => of(false)),
      takeUntil(this.unSubscribeAll$),
    ).subscribe((response: string | false) => {
      this.isSubmitDisabled = false;

      if (response) {
        let result = JSON.parse(response);
        if (Array.isArray(result)) result = result[0];

        if (result?.ISSessionExpired === true) {
          localStorage.removeItem('specialist-info');
          this.otherService.changeSpecialistUserLogin_Status(null);
          this.ngbModal.dismissAll();
          this.accountService.toastSwal('error', 'نشست شما منقضی شده است. لطفاً دوباره وارد شوید');
          this.router.navigate(['/specialist-login']);
          return;
        }

        if (!result || result.iResult === 0) {
          return this.accountService.toastSwal('error', result?.strError || 'خطایی رخ داد');
        }

        this.accountService.toastSwal('success', `عملیات با موفقیت انجام شد`);
        if (this.editId !== null && this.embedded) {
          this.savedEdit.emit(model);
          this.closeEdit.emit();
        } else {
          this.router.navigate(['/specialist-verification'], { state: { returnMobile: formValue.strMobile } });
        }
      } else {
        this.accountService.toastSwal('error', 'خطایی رخ داد');
      }
    });
  }

  invalidEditField(name: string): boolean {
    const control = this.formGroup.get(name);
    return this.editId !== null && !!control && control.invalid && control.touched;
  }

  onSignatureChanged(value: string): void {
    if (!this.canEdit) return;
    this.formGroup.get('strSignatureImage').setValue(value);
    this.formGroup.get('strSignatureImage').markAsDirty();
  }

  loadEdit(): void {
    this.isLoadingEdit = true;
    this.accountService.userInfo4Admin({
      iSystemUser: this.otherService.specialistUser.iSystemUser,
      strSession: this.otherService.specialistUser.strSession,
      iPreregistration: this.editId
    }).pipe(catchError(() => of(false)), takeUntil(this.unSubscribeAll$)).subscribe((response: string | false) => {
      this.isLoadingEdit = false;
      try {
        const parsed = response && JSON.parse(response);
        const result = Array.isArray(parsed) ? parsed[0] : parsed;
        if (result?.ISSessionExpired === true) {
          localStorage.removeItem('specialist-info');
          this.otherService.changeSpecialistUserLogin_Status(null);
          this.accountService.toastSwal('error', 'نشست شما منقضی شده است. لطفاً دوباره وارد شوید');
          this.ngbModal.dismissAll();
          this.router.navigate(['/specialist-login']);
          return;
        }
        if (!result || result.iResult === 0) {
          this.accountService.toastSwal('error', result?.strError || 'دریافت اطلاعات پیش‌ثبت‌نام ناموفق بود');
          if (this.embedded) this.closeEdit.emit();
          else this.router.navigate(['/specialist-verification']);
          return;
        }
        const plate = (result.strVehicleNo || '').trim().split(/\s+/);
        this.formGroup.patchValue({ ...result, iCost: result.iCost ? this.formatAmount(String(result.iCost)) : '', plak1: plate[0] || '', plak2: plate[1] || 'ع', plak3: plate[2] || '', plak4: plate[4] || '' });
        this.canEdit = result.bCanEdit === true || result.bCanEdit === 1 || result.bCanEdit === '1';
        this.installationStatus = result.strInstallationStatus || '';
        if (this.canEdit) this.formGroup.enable();
        else this.formGroup.disable();
        this.coordinates = { fLat: result.fLat || 0, fLon: result.fLon || 0 };
        this.selectedState = result.strState || '';
        if (this.selectedState) this.loadCities(this.selectedState);
      } catch {
        this.accountService.toastSwal('error', 'دریافت اطلاعات پیش‌ثبت‌نام ناموفق بود');
        if (this.embedded) this.closeEdit.emit();
        else this.router.navigate(['/specialist-verification']);
      }
    });
  }

  formatAmount(value: string): string {
    const digits = value.replace(/[۰-۹٠-٩]/g, char => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(char) >= 0 ? '۰۱۲۳۴۵۶۷۸۹'.indexOf(char) : '٠١٢٣٤٥٦٧٨٩'.indexOf(char))).replace(/[^0-9]/g, '').replace(/^0+(?=\d)/, '');
    return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  onAmountInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const position = input.selectionStart ?? input.value.length;
    const digitsAfter = input.value.slice(position).replace(/[^0-9۰-۹٠-٩]/g, '').length;
    const formatted = this.formatAmount(input.value);
    this.formGroup.get('iCost').setValue(formatted);
    input.value = formatted;
    let cursor = formatted.length;
    for (let remaining = digitsAfter; remaining > 0 && cursor > 0;) {
      if (/\d/.test(formatted[--cursor])) remaining--;
    }
    input.setSelectionRange(cursor, cursor);
  }

  show_Map() {
    if (this.editId !== null && !this.canEdit) return;
    this.mapService.getCurrentLocation().then(coords => {
      this.coordinates.fLon = coords.longitude;
      this.coordinates.fLat = coords.latitude;
    }).finally(() => this.showMapDialog = true);
  }

  submitMap(event: any) {
    this.formGroup.get('strAddress').setValue(event.strText);

    this.coordinates.fLon = event.longitude;
    this.coordinates.fLat = event.latitude;

    this.showMapDialog = false;
  }

  validateStrMobile(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const controlValue = control.value;
      const isValid = this.otherService.isValidMobilePhoneNumber(controlValue);

      return isValid ? null : { mobilePhoneNumberWrong: true };
    };
  }

  validateStrID(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const controlValue = control.value;
      const isValid = this.otherService.isValidNationalCode(controlValue);

      return isValid ? null : { nationalCodeWrong: true };
    };
  }

  getCities(e: Event) {
    const stateId = (e.target as HTMLSelectElement).value;
    this.loadCities(stateId);
  }

  loadCities(stateId: string) {
    this.cities$ = this.accountService.GetCity(stateId).pipe(
      map((response: string) => JSON.parse(response)),
      catchError(() => of(false)),
    );
  }

  onFileSubmit(event) {

    if (event.strFile) {

      const reader: any = new FileReader();

      reader.readAsDataURL(event.strFile);

      reader.onload = async () => {

        var base64String = reader.result.toString();

        this.formGroup.patchValue({

          strLicenseImage: base64String.replace('data:image/jpeg;base64,', ''),

        })

      };

    }

    else {

      this.formGroup.patchValue({

        strLicenseImage: ''

      })

    }

  }

  onlyNumber(e: KeyboardEvent) {

    if (e.ctrlKey || e.metaKey || /[0-9]/.test(e.key) || ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(e.key)) {

      return;

    }

    e.preventDefault();
  }

  onlyPersian(e: KeyboardEvent) {

    const controlKeys = [
      'Backspace',
      'Delete',
      'ArrowLeft',
      'ArrowRight',
      'Tab',
      'Home',
      'End'
    ];

    if (e.ctrlKey || e.metaKey || controlKeys.includes(e.key)) {
      return;
    }

    if (!this.persianRegex.test(e.key)) {

      e.preventDefault();

    }


  }

  sanitizePersian(e: any) {
    const value = e.target.value;
    e.target.value = value.replace(/[^\u0600-\u06FF\s]/g, '');

  }

  unSubscribeAll(): void {
    this.unSubscribeAll$.next();
    this.unSubscribeAll$.complete();
  };

  ngOnDestroy(): void {
    this.unSubscribeAll();
  }


}
